import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Dog, Cat, AlertTriangle, ChevronRight, Clock, MapPin, Users, Heart, ChevronLeft, MapPinOff, Phone, Mail, BadgeCheck, HelpCircle, ArrowUp } from "lucide-react";
import { trackEvent } from "@/hooks/useEventTracking";
import { shelterFields } from "@/lib/analyticsEvents";
import type { ShelterWithDistance } from "@/pages/Index";
import { isUrgentNeedsActive } from "@/types/shelter";
import { useIsTabletOrMobile } from "@/hooks/use-mobile";
import { formatDistanceToNow } from "date-fns";
import { pl } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ShareChoiceDialog } from "./ShareChoiceDialog";
import { VerificationBadge } from "./VerificationBadge";
import { UrgentBadge } from "./UrgentBadge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { EmptyState } from "./EmptyState";
import { shelterUrl } from "@/lib/shelterSlug";

interface ShelterListProps {
  shelters: ShelterWithDistance[];
  selectedShelterId: string | null;
  onShelterSelect: (shelter: ShelterWithDistance) => void;
  containerRef?: React.RefObject<HTMLDivElement>;
  onClearFilters?: () => void;
  hasActiveFilters?: boolean;
  searchQuery?: string;
  filterCity?: string;
  allShelters?: ShelterWithDistance[];
  onSuggestionClick?: (q: string) => void;
  suggestionsEnabled?: boolean;
}

const ITEMS_PER_PAGE = 4;

// Check if shelter has a proper address (not just city name)
const hasExactLocation = (shelter: ShelterWithDistance): boolean => {
  if (!shelter.address) return false;
  const addressLower = shelter.address.toLowerCase().trim();
  const cityLower = shelter.city.toLowerCase().trim();
  if (addressLower === '' || addressLower === cityLower) return false;
  if (addressLower === `${cityLower}, polska` || addressLower === `${cityLower}, poland`) return false;
  return true;
};

export const ShelterList = ({ shelters, selectedShelterId, onShelterSelect, containerRef, onClearFilters, hasActiveFilters, searchQuery, filterCity, allShelters, onSuggestionClick, suggestionsEnabled }: ShelterListProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const isTabletOrMobile = useIsTabletOrMobile();
  const listRef = useRef<HTMLDivElement>(null);

  const scrollToTop = () => {
    containerRef?.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const totalPages = Math.ceil(shelters.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  
  // On desktop show all shelters, on mobile/tablet use pagination
  const displayedShelters = isTabletOrMobile 
    ? shelters.slice(startIndex, startIndex + ITEMS_PER_PAGE)
    : shelters;

  // Reset to page 1 when shelters change (e.g., after filtering)
  if (currentPage > totalPages && totalPages > 0) {
    setCurrentPage(1);
  }

  if (shelters.length === 0) {
    return (
      <EmptyState 
        type="no-results" 
        onClearFilters={onClearFilters}
        hasActiveFilters={hasActiveFilters}
        searchQuery={searchQuery}
        city={filterCity}
        allShelters={allShelters}
        onSuggestionClick={onSuggestionClick}
        suggestionsEnabled={suggestionsEnabled}
      />
    );
  }

  const getLastUpdatedText = (dateString: string) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: pl });
    } catch {
      return null;
    }
  };

  const formatDistance = (distance: number): string => {
    if (distance < 1) {
      return `${Math.round(distance * 1000)} m`;
    }
    return `${distance.toFixed(1)} km`;
  };

  return (
    <div className="space-y-2" ref={listRef}>
      {displayedShelters.map((shelter, index) => (
        <div
          key={shelter.id}
          data-shelter-id={shelter.id}
          role="button"
          tabIndex={0}
          onClick={() => {
            trackEvent("Otworzono schronisko", { ...shelterFields(shelter), source: "list" });
            onShelterSelect(shelter);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              trackEvent("Otworzono schronisko", { ...shelterFields(shelter), source: "list" });
              onShelterSelect(shelter);
            }
          }}
          className="shelter-card group w-full text-left"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2">
                <h3 className="font-display font-semibold text-card-foreground text-sm sm:text-[15px] leading-tight flex-1 break-words">
                  {shelter.name}
                  <Link
                    to={shelterUrl(shelter)}
                    onClick={(e) => e.stopPropagation()}
                    className="sr-only"
                    aria-label={`Pełna strona schroniska ${shelter.name}`}
                  >
                    Pełna strona
                  </Link>
                </h3>
                <div className="flex items-center gap-2 sm:gap-2 flex-shrink-0">
                  <VerificationBadge verified={!!shelter.organization_verified} size="sm" />
                  <span className="opacity-60 group-hover:opacity-100 transition-opacity ml-0.5 scale-90 sm:scale-100 origin-right">
                    <ShareChoiceDialog shelter={shelter} variant="icon" />
                  </span>
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-0.5">
                <p className="text-xs sm:text-[13px] text-muted-foreground leading-tight">
                  {shelter.city}{shelter.region && `, ${shelter.region}`}
                </p>
                {hasExactLocation(shelter) ? (
                  shelter.distance !== undefined && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0 leading-4 rounded-full whitespace-nowrap flex-shrink-0">
                      <MapPin className="w-2.5 h-2.5" />
                      {formatDistance(shelter.distance)}
                    </span>
                  )
                ) : (
                  <span className="inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground bg-muted px-1.5 py-0 leading-4 rounded-full whitespace-nowrap flex-shrink-0">
                    <MapPinOff className="w-2.5 h-2.5" />
                    <span className="hidden xs:inline">Brak lokalizacji</span>
                  </span>
                )}
              </div>
              
              {/* Contact icons */}
              {(shelter.phone || shelter.email) && (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                  {shelter.phone && (
                    <a 
                      href={`tel:${shelter.phone.split(';')[0].trim()}`}
                      onClick={(e) => { e.stopPropagation(); trackEvent("Telefon", shelterFields(shelter)); }}
                      className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Phone className="w-3 h-3 flex-shrink-0" />
                      <span className="sm:hidden">{shelter.phone.split(';')[0].trim()}</span>
                      <span className="hidden sm:inline">{shelter.phone}</span>
                    </a>
                  )}
                  {shelter.email && (
                    <a 
                      href={`mailto:${shelter.email}`}
                      onClick={(e) => { e.stopPropagation(); trackEvent("Email", shelterFields(shelter)); }}
                      className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground hover:text-primary transition-colors"
                    >
                      <Mail className="w-3 h-3 flex-shrink-0" />
                      <span className="break-all">{shelter.email}</span>
                    </a>
                  )}
                </div>
              )}
              
              {/* Unverified data badge moved to top-right corner */}
              
              <div className="flex flex-wrap gap-1 mt-1.5">
                {isUrgentNeedsActive(shelter) && <UrgentBadge />}
                {shelter.dog_walking_available === true && (
                  <span className="inline-flex items-center gap-1 bg-walking text-walking-foreground text-[11px] sm:text-[10px] font-semibold px-2 sm:px-1.5 py-0.5 sm:py-0 leading-5 sm:leading-4 rounded-full">
                    <Heart className="w-3 h-3 sm:w-2.5 sm:h-2.5" />
                    <span className="hidden xs:inline">Spacery</span>
                  </span>
                )}
                {shelter.volunteering_available === true && (
                  <span className="inline-flex items-center gap-1 text-[11px] sm:text-[10px] font-semibold px-2 sm:px-1.5 py-0.5 sm:py-0 leading-5 sm:leading-4 rounded-full bg-teal-100 text-teal-700">
                    <Users className="w-3 h-3 sm:w-2.5 sm:h-2.5" />
                    <span className="hidden xs:inline">Wolontariat</span>
                  </span>
                )}
                {shelter.dogs_available === true && (
                  <span className="inline-flex items-center gap-1 text-[11px] sm:text-[10px] font-semibold px-2 sm:px-1.5 py-0.5 sm:py-0 leading-5 sm:leading-4 rounded-full bg-blue-100 text-blue-700">
                    <Dog className="w-3 h-3 sm:w-2.5 sm:h-2.5" />
                    <span className="hidden xs:inline">Psy</span>
                  </span>
                )}
                {shelter.cats_available === true && (
                  <span className="inline-flex items-center gap-1 text-[11px] sm:text-[10px] font-semibold px-2 sm:px-1.5 py-0.5 sm:py-0 leading-5 sm:leading-4 rounded-full bg-purple-100 text-purple-700">
                    <Cat className="w-3 h-3 sm:w-2.5 sm:h-2.5" />
                    <span className="hidden xs:inline">Koty</span>
                  </span>
                )}
              </div>

              {shelter.current_needs && (
                <div className="mt-1.5">
                  <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-0.5">
                    Aktualnie potrzebne
                  </p>
                  <div className="relative">
                    <p className="text-[11px] sm:text-[12px] leading-snug text-foreground/80 line-clamp-2">
                      {shelter.current_needs}
                    </p>
                    {shelter.current_needs.length > 110 && (
                      <>
                        <div className="pointer-events-none absolute bottom-0 right-0 h-4 w-14 bg-gradient-to-l from-card to-transparent" />
                        <span className="inline-flex items-center gap-0.5 mt-1 text-[11px] font-semibold text-primary px-1.5 py-0.5 -ml-1.5 rounded-md bg-primary/0 group-hover:bg-primary/10 transition-colors">
                          Zobacz więcej <ChevronRight className="w-3 h-3" />
                        </span>
                      </>
                    )}
                  </div>
                </div>
              )}

              {/* Last updated */}
              {shelter.last_updated && (
                <p className="text-[10px] text-muted-foreground/60 flex items-center gap-1 min-w-0 mt-1">
                  <Clock className="w-2.5 h-2.5 shrink-0" />
                  <span className="sm:hidden truncate">{getLastUpdatedText(shelter.last_updated)}</span>
                  <span className="hidden sm:inline truncate">Aktualizacja {getLastUpdatedText(shelter.last_updated)}</span>
                </p>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Pagination - on mobile and tablet */}
      {isTabletOrMobile && totalPages > 1 && (
        <div className="flex items-center justify-between gap-2 pt-3 mt-1 border-t border-border/60">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            className="h-8 px-2 gap-1 text-xs"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden xs:inline">Poprzednia</span>
          </Button>
          
          <span className="text-xs text-muted-foreground tabular-nums">
            {currentPage} / {totalPages}
          </span>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            className="h-8 px-2 gap-1 text-xs"
          >
            <span className="hidden xs:inline">Następna</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}

      {/* Scroll to top - only on desktop when there are many shelters */}
      {!isTabletOrMobile && shelters.length > 4 && (
        <div className="pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={scrollToTop}
            className="w-full gap-2"
          >
            <ArrowUp className="w-4 h-4" />
            Przewiń do góry
          </Button>
        </div>
      )}
    </div>
  );
};
